import { IMGS_PATH } from 'constants/image';
import { Service } from 'constants/usercentrics';
import clsx from 'clsx';
import { useEffect } from 'react';
import Markdown from 'components/Shared/Markdown';
import PhoneBox from 'components/Shared/PhoneBox';
import Layout from 'components/Shared/SimpleLayout';
import Icon from 'components/ui/Icon';
import Image from 'components/ui/Image';
import Link from 'components/ui/Link';
import Socials from 'components/ui/Socials';
import Thumbnail from 'components/ui/Thumbnail';
import config from 'config';
import { useCountry as defaultUseCountry } from 'hooks/useCountry';
import { useTranslation } from 'hooks/useTranslation';
import styles from './ThankYouPage.module.scss';
import type { UseCountry } from 'hooks/useCountry';
import type { Brand } from 'types/Brand';

type Props = {
  useCountry?: UseCountry;
  sidebar?: JSX.Element;
  brand?: Brand | null;
};

const ThankYou = ({
  useCountry = defaultUseCountry,
  sidebar,
  brand,
}: Props): JSX.Element => {
  const { t, locale } = useTranslation();
  const { country } = useCountry();
  const { facebookTrackingId } = config.defaults;

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'text/plain';
    script.dataset.usercentrics = Service.FACEBOOK_PIXEL;
    script.innerHTML = `/* Facebook Pixel Code */
    !function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window,document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
     fbq('init', ${facebookTrackingId});
    fbq('track', 'Lead'); /* Facebook Pixel Code */
    `;

    document.head.appendChild(script);

    return () => {
      if (script && script.parentNode?.nodeName === 'HEAD') {
        document.head.removeChild(script);
      }
    };
  }, []);

  const navigationItemClasses =
    'text-sm border-b border-transparent hover:border-chathams transition-color duration-200 ease-in-out';

  const countryData =
    config.countries[country as keyof typeof config.countries];

  const blogPageForLocale =
    config.pages.blogPage[locale as keyof typeof config.pages.blogPage];

  const { defaultManager } = brand || {};

  return (
    <Layout>
      <Layout.Header className="py-6 bg-wafer text-chathams">
        <div className="layout-default">
          <Link href={`/${locale}`}>
            <a className="text-primary">
              <Icon type="Qunomedical" width="11rem" />
            </a>
          </Link>
          <div className="border-b-2 border-chathams max-w-xs my-4" />
          <h1 className="text-xl font-bold mb-4">{t('thankYou.title')}</h1>
          <p>{t('thankYou.subtitle')}</p>
        </div>
      </Layout.Header>
      <Layout.Main className="text-siam">
        <div className="md:grid md:grid-cols-3 max-w-screen-xl mx-auto md:gap-x-5">
          <div className="md:col-span-2">
            <section className="layout-default pt-6">
              <h2 className="text-lg text-chathams font-bold mb-4">
                {t('thankYou.callTitle')}
              </h2>
              <PhoneBox
                useCountry={useCountry}
                className="text-xl mt-2 mb-4"
                useLink={false}
              />
              <p>{t('thankYou.callText')}</p>
              <hr className="border-silver mt-6 md:max-w-xs" />
            </section>
            <section className="layout-default pt-6">
              <h2 className="text-lg text-chathams font-bold mb-4">
                {t('thankYou.nextTitle')}
              </h2>
              <ol
                className={clsx(
                  'list-inside ml-6 border-l-2 border-light-grey',
                  styles.list,
                )}
              >
                {defaultManager ? (
                  <li
                    className={clsx(
                      'relative mb-12 flex items-center',
                      styles.listItemImage,
                    )}
                  >
                    <Thumbnail
                      src={defaultManager?.profileImage?.url || ''}
                      alt={
                        defaultManager?.profileImage?.description ||
                        'patient care manager'
                      }
                      size={48}
                      shape="circle"
                      className={styles.patientManager}
                    />
                    <Markdown>
                      {t(
                        'thankYou.nextStep1',
                        {
                          name: defaultManager?.firstname,
                        },
                        true,
                      )}
                    </Markdown>
                  </li>
                ) : (
                  <li className="relative pl-10 mb-12">
                    {t('thankYou.nextStep1.noManager')}
                  </li>
                )}
                <li className="relative pl-10 mb-12">
                  {t('thankYou.nextStep2')}
                </li>
                <li className="relative pl-10 mb-12">
                  {t('thankYou.nextStep3')}
                </li>
                <li className="relative pl-10 mb-12">
                  {t('thankYou.nextStep4')}
                </li>
              </ol>
              <hr className="border-silver mt-6 md:max-w-xs" />
            </section>
            <section className="layout-default pt-6">
              <h2 className="text-lg text-chathams font-bold mb-4">
                {t('thankYou.getInTouch')}
              </h2>

              <Link href={`mailto:${config.defaults.email}`}>
                <a className="bg-nile-blue-default hover:bg-nile-blue-light text-white text-xl font-bold max-w-xs flex items-center justify-center relative rounded shadow-lg py-2 px-4 mb-4">
                  <Icon
                    type="Email"
                    width="25"
                    height="25"
                    color="white"
                    className="absolute left-0 ml-4"
                  />
                  {t('form.email')}
                </a>
              </Link>
              <Link
                href={`https://api.whatsapp.com/send?phone=${
                  'whatsApp' in countryData && countryData.whatsApp
                    ? countryData.whatsApp
                    : config.defaults.whatsApp
                }&text=${encodeURIComponent(t('thankYou.whatsappText'))}`}
              >
                <a className="bg-whatsapp-green-default hover:bg-whatsapp-green-light text-white text-xl font-bold max-w-xs flex items-center justify-center relative rounded shadow-lg py-2 mb-4">
                  <Icon
                    type="Whatsapp"
                    width="25"
                    height="25"
                    color="white"
                    className="absolute left-0 ml-4"
                  />
                  {t('whatsapp')}
                </a>
              </Link>
              <Link href={config.defaults.messengerUrl}>
                <a className="bg-facebook-default hover:bg-facebook-light text-white text-xl font-bold max-w-xs flex items-center justify-center relative rounded shadow-lg py-2 mb-4">
                  <Icon
                    type="Messenger"
                    width="30"
                    height="30"
                    color="white"
                    className="absolute left-0 ml-3"
                  />
                  {t('facebook')}
                </a>
              </Link>
              <hr className="border-silver mt-6 md:max-w-xs" />
            </section>
            <section className="layout-default pt-6">
              <h2 className="text-lg text-chathams font-bold mb-4">
                {t('thankYou.aboutTitle')}
              </h2>
              <Link href={t('thankYou.aboutUrl')}>
                <a className="bg-secondary hover:bg-grandis text-xl font-bold max-w-xs flex items-center justify-center relative rounded shadow-lg py-2 mb-3">
                  {t('thankYou.aboutButton')}
                </a>
              </Link>
              <hr className="border-silver mt-6 md:max-w-xs" />
            </section>
            <section className="layout-default py-6">
              <h2 className="text-lg text-chathams font-bold mb-4">
                {t('thankYou.blogTitle')}
              </h2>
              <Link href={`/${locale}/${blogPageForLocale}`}>
                <a className="bg-secondary hover:bg-grandis text-xl font-bold max-w-xs flex items-center justify-center relative rounded shadow-lg py-2 mb-3">
                  {t('thankYou.blogButton')}
                </a>
              </Link>
            </section>
          </div>
          {sidebar && <div>{sidebar}</div>}
        </div>
        <section className="pt-6 pb-10 bg-grey-cold">
          <div className="layout-default">
            <h3 className="text-secondary text-xl mb-6">
              {t('home.provenSuccess.mediasTitle')}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4">
              <div className="mx-auto md:mx-0 my-2 relative">
                <Image
                  src={`${IMGS_PATH.PUBLIC}/forbes-new.png`}
                  alt="Forbes"
                  width={160}
                  height={75}
                />
              </div>
              <div className="mx-auto md:mx-0 my-2 relative">
                <Image
                  src={`${IMGS_PATH.PUBLIC}/Beckers.png`}
                  alt="Becker's Hospital Review"
                  width={160}
                  height={75}
                />
              </div>
              <div className="mx-auto md:mx-0 my-2 relative">
                <Image
                  src={`${IMGS_PATH.PUBLIC}/huffpo.jpg`}
                  alt="The Huffington Post"
                  width={160}
                  height={75}
                />
              </div>
              <div className="mx-auto md:mx-0 my-2 relative">
                <Image
                  src={`${IMGS_PATH.PUBLIC}/fas-new.png`}
                  alt="Frankfurter Allgemeine Sonntagszeitung"
                  width={160}
                  height={75}
                />
              </div>
            </div>
          </div>
        </section>
      </Layout.Main>
      <Layout.Footer className="py-6 bg-wafer text-chathams">
        <div className="layout-default">
          <Link href={`/${locale}`}>
            <a className="text-primary">
              <Icon type="Qunomedical" width="11rem" />
            </a>
          </Link>
          <div className="border-b-2 border-chathams max-w-xs my-4" />
          <PhoneBox
            useCountry={useCountry}
            className="bg-white hover:bg-light-grey text-chathams font-bold mb-2"
          />
          <p className="font-bold text-sm mb-2 md:mb-0">
            {t('footer.copy', { year: new Date().getFullYear() })}
          </p>
          <ul className="mb-8">
            <li className="mt-4">
              <Link href={`/${locale}/${t('form.acceptTC.link.tc')}`}>
                <a className={navigationItemClasses}>{t('footer.terms')}</a>
              </Link>
            </li>
            <li className="mt-4">
              <Link href={`/${locale}/${t('form.acceptTC.link.privacy')}`}>
                <a className={navigationItemClasses}>
                  {t('footer.privacyPolicy')}
                </a>
              </Link>
            </li>
            <li className="mt-4">
              <Link href={`/${locale}/${t('form.acceptTC.link.imprint')}`}>
                <a className={navigationItemClasses}>{t('footer.imprint')}</a>
              </Link>
            </li>
          </ul>
          <Socials
            color="#fff4dd"
            bgClass="bg-chathams"
            hoverClass="hover:bg-calypso"
            rounded={false}
          />
        </div>
      </Layout.Footer>
      <noscript>
        <img
          height="1"
          width="1"
          src={`https://www.facebook.com/tr?id=${facebookTrackingId}&ev=Lead&noscript=1`}
          className="hidden"
        />
      </noscript>
    </Layout>
  );
};

export default ThankYou;
